import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { airports } from "@/data/airports";
import { getTerminalInfo, getTerminalGroups } from "@/data/airlineTerminals";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SchemaMarkup, generateBreadcrumbSchema, generateFAQSchema } from "@/components/SchemaMarkup";
import { LiveWaitStrip } from "@/components/airport/LiveWaitStrip";
import { getDiningInfo } from "@/data/airportDining";
import { AppStoreCTA } from "@/components/AppStoreCTA";
import NotFound from "./NotFound";

const AirportTerminals = () => {
  const { code } = useParams<{ code: string }>();
  const upper = code?.toUpperCase() ?? "";
  const lower = code?.toLowerCase() ?? "";
  const hasDining = Boolean(getDiningInfo(code?.toUpperCase() ?? ""));
  const airport = airports.find((a) => a.code === upper);
  const info = getTerminalInfo(upper);

  if (!airport || !info) return <NotFound />;

  const cityName = airport.city.split(",")[0];
  const terminalGroups = getTerminalGroups(upper);
  const url = `https://airportbuddy.app/airports/${lower}/terminals`;


  const faqs = [
    {
      question: `How many terminals does ${upper} have?`,
      answer: `${airport.name} (${upper}) serves ${info.airlines.length} major airlines across its terminals. ${info.connection}`,
    },
    {
      question: `Are ${upper} terminals connected after security?`,
      answer: info.connection,
    },
    {
      question: `How do I know which terminal my flight leaves from at ${upper}?`,
      answer: `Your boarding pass shows the terminal and gate. Use the airline list on this page as a quick reference, then confirm on the day you fly — assignments do change.`,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{`${upper} Terminals – Terminal Map by Airline & Live Security Wait`}</title>
        <meta
          name="description"
          content={`Every terminal at ${airport.name} (${upper}): which airline uses which terminal, how to move between them, and the TSA security wait right now.`}
        />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`${upper} Terminals – Airlines & Live Security Wait`} />
        <meta property="og:description" content={`Which terminal does your airline use at ${upper}? Full terminal map plus live security wait times.`} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Helmet>


      <SchemaMarkup schema={generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Airports", url: "/airports" },
        { name: upper, url: `/airports/${lower}` },
        { name: "Terminals", url: `/airports/${lower}/terminals` },
      ])} />
      <SchemaMarkup schema={generateFAQSchema(faqs)} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 pt-4 pb-12">
          <Breadcrumbs items={[
            { label: "Home", href: "/" },
            { label: "Airports", href: "/airports" },
            { label: upper, href: `/airports/${lower}` },
            { label: "Terminals", href: `/airports/${lower}/terminals` },
          ]} />

          <div className="max-w-3xl mx-auto space-y-6">
            <header className="space-y-2 mt-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                Terminals at {upper}
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                Which terminal your airline uses at {airport.name}, {cityName}.
              </p>
            </header>

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-4">
                <h2 className="text-xl font-bold text-foreground">Jump to a terminal at {upper}</h2>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {terminalGroups.map((g) => (
                    <li key={g.slug}>
                      <Link
                        to={`/airports/${lower}/terminals/${g.slug}`}
                        className="flex items-center justify-between gap-2 rounded-xl border px-4 py-3 min-h-[44px] text-base hover:bg-accent/50 active:scale-95 transition"
                      >
                        <span className="font-medium">{g.label}</span>
                        <Badge variant="secondary" className="shrink-0">
                          {g.airlines.length} airline{g.airlines.length === 1 ? "" : "s"}
                        </Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6">
                <h2 className="text-xl font-bold text-foreground mb-2">
                  Which terminal is my airline at {upper}?
                </h2>

                <ul className="divide-y">
                  {info.airlines.map((a) => (
                    <li key={a.slug}>
                      <Link
                        to={`/airports/${lower}/terminals/${a.slug}`}
                        className="flex items-center justify-between gap-3 py-3 min-h-[44px] active:scale-95 transition"
                      >
                        <span className="text-base font-medium text-foreground">{a.airline}</span>
                        <Badge variant="secondary" className="shrink-0">{a.terminal}</Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground border-t pt-3 mt-3">
                  Terminal assignments change. Confirm on your boarding pass or at{" "}
                  <a
                    href={info.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline inline-flex items-center gap-1"
                  >
                    the official {upper} site
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                  .
                </p>
              </CardContent>
            </Card>

            <LiveWaitStrip airportCode={upper} />

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-2">
                <h2 className="text-xl font-bold text-foreground">Getting between terminals at {upper}</h2>
                <p className="text-base text-muted-foreground">{info.connection}</p>
              </CardContent>
            </Card>

            <AppStoreCTA
              placement="airport_footer"
              context={upper}
              headline={`Know your terminal. Now know your timing.`}
              subline={`Airport Buddy watches the ${upper} security line and pushes you a leave-by reminder. Free on iPhone.`}
            />

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}`}>{upper} live wait times</Link>
              </Button>
              <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}/guide`}>Full {upper} guide</Link>
              </Button>
              <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}/parking`}>{upper} parking</Link>
              </Button>
              {hasDining && (
                <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                  <Link to={`/airports/${lower}/dining`}>{upper} restaurants</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AirportTerminals;
